// Admin > Showcase (ticket MS-UI, spec-v4-models.md section 2 / V4-MS): the model showcase job's data, read from
// GET /api/admin/model-showcase and POST /api/admin/model-showcase/plan, checked here, and turned into the facts the
// screen shows (the same ones api/scripts/showcase.ts planLines / statusLine print). Pure; tested in
// showcase-admin.test.ts. The job spends real provider money (ours, never a user's), capped per round.

export type ShowcaseJobState = "idle" | "running" | "stopped" | "capped" | "done";

export type AdminShowcasePlan = {
  round: number;
  capUsd: number;
  spentUsd: number;
  remainingUsd: number;
  imageModel: string;
  styles: { id: string; name: string }[];
  models: { id: string; label: string }[];
  counts: { cells: number; done: number; running: number; failed: number; gaveUp: number; todo: number; frames: number; framesDone: number };
  estimate: {
    usd: number;
    imageModel: { id: string; label: string; usd: number };
    frames: { count: number; usd: number };
    clips: { count: number; usd: number };
    byModel: { id: string; label: string; clipUsd: number; durationS: number; cells: number; usd: number }[];
  };
  fits: boolean;
};

export type AdminShowcaseStatus = {
  state: ShowcaseJobState;
  round: number;
  capUsd: number;
  styles: string[];
  models: string[];
  startedAt: string | null;
  updatedAt: string | null;
  message: string | null;
  plan: AdminShowcasePlan;
  running: { styleId: string; modelId: string | null; since: string | null }[];
  recentFailures: { styleId: string; modelId: string; error: string | null; attempts: number }[];
};

/** The cap the screen starts with (Keaton, 2026-10-29: the full showcase is ~$120, hard cap $150). */
export const DEFAULT_CAP_USD = 150;
/** Auto-refresh while the job runs. */
export const REFRESH_MS = 15_000;
/** How long the "Start · about $X" confirm waits for its second tap. */
export const CONFIRM_MS = 8_000;

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const num = (v: unknown, d = 0): number => (typeof v === "number" && Number.isFinite(v) ? v : d);
const str = (v: unknown, d = ""): string => (typeof v === "string" ? v : d);
const strOrNull = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const STATES: ShowcaseJobState[] = ["idle", "running", "stopped", "capped", "done"];

/** Reads a plan defensively: a missing field is a zero, never a crash. */
export function parsePlan(raw: unknown): AdminShowcasePlan {
  const p = obj(raw);
  const c = obj(p.counts);
  const e = obj(p.estimate);
  const pair = (v: unknown) => ({ count: num(obj(v).count), usd: num(obj(v).usd) });
  return {
    round: num(p.round, 1),
    capUsd: num(p.capUsd),
    spentUsd: num(p.spentUsd),
    remainingUsd: num(p.remainingUsd),
    imageModel: str(p.imageModel),
    styles: arr(p.styles).map(obj).filter((s) => typeof s.id === "string").map((s) => ({ id: s.id as string, name: str(s.name, s.id as string) })),
    models: arr(p.models).map(obj).filter((m) => typeof m.id === "string").map((m) => ({ id: m.id as string, label: str(m.label, m.id as string) })),
    counts: { cells: num(c.cells), done: num(c.done), running: num(c.running), failed: num(c.failed), gaveUp: num(c.gaveUp), todo: num(c.todo), frames: num(c.frames), framesDone: num(c.framesDone) },
    estimate: {
      usd: num(e.usd),
      imageModel: { id: str(obj(e.imageModel).id), label: str(obj(e.imageModel).label, str(obj(e.imageModel).id)), usd: num(obj(e.imageModel).usd) },
      frames: pair(e.frames),
      clips: pair(e.clips),
      byModel: arr(e.byModel)
        .map(obj)
        .filter((m) => typeof m.id === "string")
        .map((m) => ({ id: m.id as string, label: str(m.label, m.id as string), clipUsd: num(m.clipUsd), durationS: num(m.durationS), cells: num(m.cells), usd: num(m.usd) })),
    },
    fits: p.fits === true,
  };
}

export function parseStatus(raw: unknown): AdminShowcaseStatus {
  const s = obj(raw);
  const plan = parsePlan(s.plan);
  return {
    state: STATES.includes(s.state as ShowcaseJobState) ? (s.state as ShowcaseJobState) : "idle",
    round: num(s.round, plan.round),
    capUsd: num(s.capUsd, plan.capUsd),
    styles: arr(s.styles).filter((x): x is string => typeof x === "string"),
    models: arr(s.models).filter((x): x is string => typeof x === "string"),
    startedAt: strOrNull(s.startedAt),
    updatedAt: strOrNull(s.updatedAt),
    message: strOrNull(s.message),
    plan,
    running: arr(s.running)
      .map(obj)
      .filter((r) => typeof r.styleId === "string")
      .map((r) => ({ styleId: r.styleId as string, modelId: strOrNull(r.modelId), since: strOrNull(r.since) })),
    recentFailures: arr(s.recentFailures)
      .map(obj)
      .filter((r) => typeof r.styleId === "string")
      .map((r) => ({ styleId: r.styleId as string, modelId: str(r.modelId), error: strOrNull(r.error), attempts: num(r.attempts) })),
  };
}

/** "$137.64", like core's usdWords. */
export const usd = (n: number) => `$${(Math.round(n * 100) / 100).toFixed(2)}`;

export const STATE_LABEL: Record<ShowcaseJobState, string> = {
  idle: "Not started",
  running: "Running",
  stopped: "Stopped",
  capped: "Stopped at the cap",
  done: "Done",
};

/** A style's name and a model's label from the plan, falling back to the id. */
export const styleName = (p: AdminShowcasePlan, id: string) => p.styles.find((s) => s.id === id)?.name ?? id;
export const modelName = (p: AdminShowcasePlan, id: string | null) => (id === null ? "start frame" : p.models.find((m) => m.id === id)?.label ?? p.estimate.byModel.find((m) => m.id === id)?.label ?? id);

/** The status card's facts (statusLine's, plus the round and what failed). */
export function statusFacts(s: AdminShowcaseStatus) {
  const p = s.plan;
  const now = s.running[0];
  return {
    state: STATE_LABEL[s.state],
    round: `Round ${p.round}`,
    clips: `${p.counts.done} / ${p.counts.cells}`,
    frames: `${p.counts.framesDone} / ${p.counts.frames}`,
    spent: `${usd(p.spentUsd)} of ${usd(p.capUsd)}`,
    /** 0..1, for the spend bar */
    spentShare: p.capUsd > 0 ? Math.min(1, p.spentUsd / p.capUsd) : 0,
    now: now ? `${styleName(p, now.styleId)}: ${modelName(p, now.modelId)}` : null,
    failures: s.recentFailures.map((f) => ({
      what: `${styleName(p, f.styleId)}: ${modelName(p, f.modelId)}`,
      error: f.error ?? "No reason given.",
      tries: f.attempts === 1 ? "1 try" : `${f.attempts} tries`,
    })),
    message: s.message,
    narrowed: s.styles.length > 0 || s.models.length > 0,
  };
}

/** The dry run as the screen shows it: planLines's header counts, the per-model table and the totals. */
export function planView(p: AdminShowcasePlan) {
  return {
    title: `Round ${p.round}: ${p.styles.length} styles × ${p.models.length} models = ${p.counts.cells} clips`,
    progress: `Made ${p.counts.done}, failed (will retry once) ${p.counts.failed}, given up ${p.counts.gaveUp}, running ${p.counts.running}, to make ${p.counts.todo}`,
    frames: `Start frames: ${p.counts.framesDone}/${p.counts.frames} made; ${p.estimate.frames.count} to make with ${p.estimate.imageModel.label || p.imageModel} (${usd(p.estimate.frames.usd)})`,
    rows: p.estimate.byModel.map((m) => ({ id: m.id, label: m.label, clip: usd(m.clipUsd), len: `${m.durationS}s`, toMake: m.cells, usd: usd(m.usd) })),
    estimate: `${usd(p.estimate.usd)} (${p.estimate.clips.count} clips ${usd(p.estimate.clips.usd)} + ${p.estimate.frames.count} frames ${usd(p.estimate.frames.usd)})`,
    spent: `${usd(p.spentUsd)} of the ${usd(p.capUsd)} cap`,
    fits: p.fits,
    fitsLine: p.fits ? "Fits under the cap." : "Won't fit: it will stop at the cap.",
    nothingLeft: p.counts.todo === 0 && p.estimate.frames.count === 0,
  };
}

/** The Start button: the first tap arms it, the second (this label) starts it. */
export const startLabel = (p: AdminShowcasePlan | null, armed: boolean) => (armed && p ? `Start · about ${usd(p.estimate.usd)}` : "Start");

/** The cap field: a positive number of dollars, or why not. (The api also caps it at SHOWCASE_USD_CAP.) */
export function readCap(text: string): { usd: number } | { error: string } {
  const n = Number(text.trim().replace(/^\$/, ""));
  if (!text.trim() || !Number.isFinite(n) || n <= 0) return { error: "Enter a cap above $0." };
  if (n > 10_000) return { error: "That cap is too high." };
  return { usd: Math.round(n * 100) / 100 };
}

// ------------------------------------------------------------------ MS-IMG: the image round
// GET /api/admin/model-showcase/images and POST .../images/{plan,start,stop}: its own job, table and cap ($40 by
// default). Same screen, its own section; the same two-tap Start. Reused start frames (the video round's, drawn by the
// same model from the same prompt) cost $0.

export type AdminImagePlan = {
  round: number;
  capUsd: number;
  spentUsd: number;
  remainingUsd: number;
  frameModel: string;
  refs: boolean;
  rows: { id: string; name: string; kind: "style" | "ref" }[];
  models: { id: string; label: string; credits: number }[];
  counts: { cells: number; done: number; running: number; failed: number; gaveUp: number; todo: number; reused: number };
  estimate: {
    usd: number;
    images: { count: number; usd: number };
    reused: number;
    byModel: { id: string; label: string; usd: number; refUsd: number | null; credits: number; refCredits: number | null; cells: number; refCells: number; reused: number; total: number }[];
  };
  fits: boolean;
};

export type AdminImageStatus = {
  state: ShowcaseJobState;
  round: number;
  capUsd: number;
  styles: string[];
  models: string[];
  refs: boolean;
  startedAt: string | null;
  updatedAt: string | null;
  message: string | null;
  plan: AdminImagePlan;
  running: { rowId: string; modelId: string; since: string | null }[];
  recentFailures: { rowId: string; modelId: string; error: string | null; attempts: number }[];
};

/** The image round's default cap (SHOWCASE_IMAGE_USD_CAP; the full round is about $10). */
export const DEFAULT_IMAGE_CAP_USD = 40;

export function parseImagePlan(raw: unknown): AdminImagePlan {
  const p = obj(raw);
  const c = obj(p.counts);
  const e = obj(p.estimate);
  return {
    round: num(p.round, 1),
    capUsd: num(p.capUsd),
    spentUsd: num(p.spentUsd),
    remainingUsd: num(p.remainingUsd),
    frameModel: str(p.frameModel),
    refs: p.refs !== false,
    rows: arr(p.rows).map(obj).filter((r) => typeof r.id === "string").map((r) => ({ id: r.id as string, name: str(r.name, r.id as string), kind: r.kind === "ref" ? "ref" : "style" })),
    models: arr(p.models).map(obj).filter((m) => typeof m.id === "string").map((m) => ({ id: m.id as string, label: str(m.label, m.id as string), credits: num(m.credits) })),
    counts: { cells: num(c.cells), done: num(c.done), running: num(c.running), failed: num(c.failed), gaveUp: num(c.gaveUp), todo: num(c.todo), reused: num(c.reused) },
    estimate: {
      usd: num(e.usd),
      images: { count: num(obj(e.images).count), usd: num(obj(e.images).usd) },
      reused: num(e.reused),
      byModel: arr(e.byModel)
        .map(obj)
        .filter((m) => typeof m.id === "string")
        .map((m) => ({
          id: m.id as string, label: str(m.label, m.id as string), usd: num(m.usd), refUsd: typeof m.refUsd === "number" ? m.refUsd : null, credits: num(m.credits),
          refCredits: typeof m.refCredits === "number" ? m.refCredits : null,
          cells: num(m.cells), refCells: num(m.refCells), reused: num(m.reused), total: num(m.total),
        })),
    },
    fits: p.fits === true,
  };
}

export function parseImageStatus(raw: unknown): AdminImageStatus {
  const s = obj(raw);
  const plan = parseImagePlan(s.plan);
  return {
    state: STATES.includes(s.state as ShowcaseJobState) ? (s.state as ShowcaseJobState) : "idle",
    round: num(s.round, plan.round),
    capUsd: num(s.capUsd, plan.capUsd),
    styles: arr(s.styles).filter((x): x is string => typeof x === "string"),
    models: arr(s.models).filter((x): x is string => typeof x === "string"),
    refs: s.refs !== false,
    startedAt: strOrNull(s.startedAt),
    updatedAt: strOrNull(s.updatedAt),
    message: strOrNull(s.message),
    plan,
    running: arr(s.running).map(obj).filter((r) => typeof r.rowId === "string").map((r) => ({ rowId: r.rowId as string, modelId: str(r.modelId), since: strOrNull(r.since) })),
    recentFailures: arr(s.recentFailures).map(obj).filter((r) => typeof r.rowId === "string").map((r) => ({ rowId: r.rowId as string, modelId: str(r.modelId), error: strOrNull(r.error), attempts: num(r.attempts) })),
  };
}

const rowName = (p: AdminImagePlan, id: string) => p.rows.find((r) => r.id === id)?.name ?? id;
const imageModelName = (p: AdminImagePlan, id: string) => p.models.find((m) => m.id === id)?.label ?? p.estimate.byModel.find((m) => m.id === id)?.label ?? id;

/** The image round's status card facts. */
export function imageStatusFacts(s: AdminImageStatus) {
  const p = s.plan;
  const now = s.running[0];
  return {
    state: STATE_LABEL[s.state],
    round: `Round ${p.round}`,
    pictures: `${p.counts.done} / ${p.counts.cells}`,
    spent: `${usd(p.spentUsd)} of ${usd(p.capUsd)}`,
    spentShare: p.capUsd > 0 ? Math.min(1, p.spentUsd / p.capUsd) : 0,
    now: now ? `${rowName(p, now.rowId)}: ${imageModelName(p, now.modelId)}` : null,
    failures: s.recentFailures.map((f) => ({ what: `${rowName(p, f.rowId)}: ${imageModelName(p, f.modelId)}`, error: f.error ?? "No reason given.", tries: f.attempts === 1 ? "1 try" : `${f.attempts} tries` })),
    message: s.message,
    narrowed: s.styles.length > 0 || s.models.length > 0 || !s.refs,
  };
}

/** The image round's dry run as the screen shows it: header counts, the per-model table (with the price users pay) and totals. */
export function imagePlanView(p: AdminImagePlan) {
  const styles = p.rows.filter((r) => r.kind === "style").length;
  const refs = p.rows.length - styles;
  return {
    title: `Round ${p.round}: ${styles} styles${refs ? ` + ${refs} reference rows` : ""} × ${p.models.length} models = ${p.counts.cells} pictures`,
    progress: `Made ${p.counts.done}, failed (will retry once) ${p.counts.failed}, given up ${p.counts.gaveUp}, running ${p.counts.running}, to make ${p.counts.todo}`,
    reused: p.counts.reused ? `${p.counts.reused} reuse the video showcase's start frames (same model, same prompt): $0.` : null,
    rows: p.estimate.byModel.map((m) => ({
      id: m.id, label: m.label, image: usd(m.usd), withRef: m.refUsd === null ? "–" : usd(m.refUsd), credits: m.credits,
      // PRICING-1: "15 / 25" when a picture drawn from references costs more (GPT Image, FLUX.2 Pro)
      creditsLabel: m.refCredits !== null && m.refCredits !== m.credits ? `${m.credits} / ${m.refCredits}` : String(m.credits),
      toMake: m.refCells ? `${m.cells} + ${m.refCells}` : String(m.cells), reused: m.reused, usd: usd(m.total),
    })),
    estimate: `${usd(p.estimate.usd)} (${p.estimate.images.count} pictures${p.estimate.reused ? `, ${p.estimate.reused} reused for $0` : ""})`,
    spent: `${usd(p.spentUsd)} of the ${usd(p.capUsd)} cap`,
    fits: p.fits,
    fitsLine: p.fits ? "Fits under the cap." : "Won't fit: it will stop at the cap.",
    nothingLeft: p.counts.todo === 0,
  };
}

/** The image round's Start button: the first tap arms it, the second starts it. */
export const imageStartLabel = (p: AdminImagePlan | null, armed: boolean) => (armed && p ? `Start · about ${usd(p.estimate.usd)}` : "Start");
